import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AuthModalPromo } from '@/components/auth/AuthModalPromo'
import { AuthModalTabs } from '@/components/auth/AuthModalTabs'
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm'
import { LoginForm } from '@/components/auth/LoginForm'
import { RegisterForm } from '@/components/auth/RegisterForm'
import { UsernameOnboardingForm } from '@/components/auth/UsernameOnboardingForm'
import { useAuthModal, type AuthModalView } from '@/context/AuthModalContext'
import { useAppSelector } from '@/store/hooks'
import { selectUsernameRequired } from '@/store/slices/authSlice'
import { cn } from '@/lib/utils'

const TAB_ORDER: Record<AuthModalView, number> = {
	'login': 0,
	'forgot-password': 0,
	'register': 1,
	'username': 2,
}

const VIEW_COPY: Record<AuthModalView, { title: string; description: string }> = {
	'login': {
		title: 'Login',
		description: 'Sign in to your BetsCycle account',
	},
	'register': {
		title: 'Register',
		description: 'Create your BetsCycle account',
	},
	'forgot-password': {
		title: 'Forgot password',
		description: 'Reset your BetsCycle account password',
	},
	'username': {
		title: 'Choose a username',
		description: 'Choose a username to finish setting up your account',
	},
}

const formVariants = {
	enter: (direction: number) => ({
		opacity: 0,
		x: direction * 24,
	}),
	center: {
		opacity: 1,
		x: 0,
	},
	exit: (direction: number) => ({
		opacity: 0,
		x: direction * -24,
	}),
}

function renderAuthForm(view: AuthModalView) {
	if (view === 'login') {
		return <LoginForm />
	}

	if (view === 'forgot-password') {
		return <ForgotPasswordForm />
	}

	if (view === 'username') {
		return <UsernameOnboardingForm />
	}

	return <RegisterForm />
}

export function AuthModal() {
	const { view, isOpen, close, setView, isUsernameRequired } = useAuthModal()
	const usernameRequiredFromApi = useAppSelector(selectUsernameRequired)
	const isUsernameLocked = isUsernameRequired || usernameRequiredFromApi
	const previousViewRef = useRef<AuthModalView>(view)
	const [direction, setDirection] = useState(0)

	useEffect(() => {
		if (usernameRequiredFromApi) {
			setView('username')
		}
	}, [setView, usernameRequiredFromApi])

	useEffect(() => {
		const previousView = previousViewRef.current

		if (previousView && view && previousView !== view) {
			setDirection(TAB_ORDER[view] > TAB_ORDER[previousView] ? 1 : -1)
			previousViewRef.current = view
		} else if (view) {
			previousViewRef.current = view
		}
	}, [view])

	if (!view) {
		return null
	}

	const copy = VIEW_COPY[view]

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && !isUsernameLocked) close()
			}}
		>
			<Dialog.Portal>
				<Dialog.Overlay className='fixed inset-0 z-[1000] bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
				<Dialog.Content
					className={cn(
						'fixed left-1/2 top-1/2 z-[1000] flex max-h-[90vh] w-[calc(100%-2rem)] max-w-[817px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_24px_80px_0_hsl(var(--background)/0.55)] outline-none',
						'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
					)}
					onOpenAutoFocus={(event) => {
						const content = event.currentTarget as HTMLElement
						const firstField = content.querySelector<HTMLElement>("input, button:not([aria-label='Close'])")
						firstField?.focus()
						event.preventDefault()
					}}
				>
					<div className='flex max-h-[90vh] min-h-0 w-full flex-col overflow-y-auto overflow-x-hidden p-1 [scrollbar-width:none] lg:flex-row lg:items-stretch lg:overflow-hidden [&::-webkit-scrollbar]:hidden'>
						<AuthModalPromo />
						<div className='flex min-h-0 w-full flex-1 flex-col items-end justify-center self-stretch overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
							{!isUsernameLocked ? (
								<Dialog.Close
									className='m-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
									aria-label='Close'
								>
									<X className='h-5 w-5' />
								</Dialog.Close>
							) : null}
							<div className='relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-hidden px-5 pb-6 sm:px-8 sm:pb-8'>
								<Dialog.Title className='sr-only'>{copy.title}</Dialog.Title>
								<Dialog.Description className='sr-only'>{copy.description}</Dialog.Description>

								<div className={cn('mb-6 flex shrink-0 flex-col items-center gap-5 lg:mb-8 lg:items-stretch lg:gap-0', isUsernameLocked && 'mb-4 lg:mb-5')}>
									<img src='/images/auth/logo.png' alt='Bets cycle' className='h-8 w-auto object-contain lg:hidden' />
									{!isUsernameLocked ? (
										<div className='w-full'>
											<AuthModalTabs activeView={view} onChange={setView} />
										</div>
									) : null}
								</div>

								<div className='relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
									<AnimatePresence mode='wait' initial={false} custom={direction}>
										<motion.div
											key={view}
											custom={direction}
											variants={formVariants}
											initial='enter'
											animate='center'
											exit='exit'
											transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
											className='w-full'
										>
											{renderAuthForm(view)}
										</motion.div>
									</AnimatePresence>
								</div>
							</div>
						</div>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
